/* eslint-disable no-restricted-syntax */
const NETWORK_MESSAGE = 'Network Error';

const RESPONSE_HEADERS_FOR_DEBUG = [
  'x-request-id',
  'x-correlation-id',
  'x-trace-id',
  'cf-ray',
  'x-amzn-requestid',
  'server-timing',
  'retry-after',
  'content-type',
];

const SENSITIVE_HEADERS = new Set(['authorization', 'cookie', 'set-cookie', 'x-auth-token', 'x-csrf-token']);

function resolveRequestPath(config) {
  if (!config || typeof config.url !== 'string' || !config.url) {
    return null;
  }

  const path = config.url.split('?')[0];

  if (path.startsWith('http://') || path.startsWith('https://')) {
    return new URL(path).pathname;
  }

  const base = typeof config.baseURL === 'string' ? config.baseURL.replace(/\/$/, '') : '';

  if (path.startsWith('/')) {
    if (base && !path.startsWith(base)) {
      return `${base}${path}`;
    }

    return path;
  }

  return base ? `${base}/${path}` : `/${path}`;
}

const NUMERIC_ROUTE_SEGMENT = /^\d+$/;
const UUID_ROUTE_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function normalizeRouteSegment(segment) {
  if (NUMERIC_ROUTE_SEGMENT.test(segment) || UUID_ROUTE_SEGMENT.test(segment)) {
    return ':id';
  }

  return segment;
}

function normalizeRouteForGrouping(path) {
  return path
    .split('/')
    .map(normalizeRouteSegment)
    .join('/');
}

function routeForErrorName(path) {
  if (!path || typeof path !== 'string') {
    return '/unknown';
  }

  const clean = path.split('?')[0].trim();

  if (!clean) {
    return '/unknown';
  }

  const withSlash = clean.startsWith('/') ? clean : `/${clean}`;

  return normalizeRouteForGrouping(withSlash);
}

function extractBusinessCode(response) {
  const data = response?.data;

  if (data == null) {
    return null;
  }
  if (Array.isArray(data) && data[0] != null && typeof data[0] === 'object' && data[0].code != null) {
    return data[0].code;
  }
  if (typeof data === 'object' && data.code != null) {
    return data.code;
  }

  return null;
}

// Error-name buckets (Bugsnag groupingHash = _errorName):
//
//   STORE_API_ERROR_NETWORK_*     — no HTTP response (client connectivity)
//   STORE_API_ERROR_GATEWAY       — edge/nginx answered 502/503/504 with HTML maintenance
//   STORE_API_ERROR_<status>_…    — real API JSON/body from our backend (route matters)
const GATEWAY_STATUSES = new Set([502, 503, 504]);
const STORE_API_ERROR_GATEWAY = 'STORE_API_ERROR_GATEWAY';

// SERVER error = the backend answered with an HTTP status. This is usually OUR bug, so
// the failing route matters → group by status + business code + PATH (routes stay split).
function buildStoreApiServerErrorName(status, config, response) {
  const route = routeForErrorName(resolveRequestPath(config));
  const bizCode = extractBusinessCode(response);
  const bizPart = bizCode != null ? `_BIZ_${bizCode}` : '';

  return `STORE_API_ERROR_${status}${bizPart}_${route}`;
}

// NETWORK / connectivity error = NO HTTP response. The path is irrelevant (it fails the
// same way on any route), so collapse ALL of them into ONE bucket per sub-type — never
// by path. Sub-types:
//   TIMEOUT    — request aborted / timed out (ECONNABORTED / ETIMEDOUT)
//   CONNECTION — Axios "Network Error" (offline, DNS, connection refused/reset)
//   UNKNOWN    — no response, none of the above
function buildStoreApiNetworkErrorName(type) {
  return `STORE_API_ERROR_NETWORK_${type}`;
}

function getHeaderValue(headers, name) {
  if (!headers || typeof headers !== 'object') {
    return '';
  }
  const want = name.toLowerCase();

  for (const key of Object.keys(headers)) {
    if (String(key).toLowerCase() === want) {
      const val = headers[key];

      return val == null ? '' : String(val);
    }
  }

  return '';
}

// Edge/nginx often maps upstream 5xx to HTML maintenance.html — not an app JSON error.
export function isStoreApiGatewayResponse(response) {
  if (!response || !GATEWAY_STATUSES.has(response.status)) {
    return false;
  }

  const contentType = getHeaderValue(response.headers, 'content-type').toLowerCase();

  if (contentType.includes('text/html')) {
    return true;
  }

  const data = response.data;

  if (typeof data === 'string' && /ganttpro\s+maintenance/i.test(data)) {
    return true;
  }

  return false;
}

export function isStoreApiGatewayFailure(error) {
  return error != null && isStoreApiGatewayResponse(error.response);
}

// Axios "Network Error" + no HTTP response → client connectivity, not a server error body.
// Timeouts and 4xx/5xx are NOT network failures (server may still be the cause).
export function isStoreApiNetworkFailure(error) {
  return error != null
    && error.response == null
    && error.message === NETWORK_MESSAGE;
}

function pickResponseHeadersForDebug(headers) {
  if (!headers || typeof headers !== 'object') {
    return undefined;
  }
  const lower = {};

  for (const k of Object.keys(headers)) {
    lower[String(k).toLowerCase()] = headers[k];
  }
  const out = {};

  for (const key of RESPONSE_HEADERS_FOR_DEBUG) {
    const raw = lower[key];

    if (raw == null) continue;
    const v = typeof raw === 'string' ? raw : String(raw);

    out[key] = v.length > 500 ? `${v.slice(0, 500)}\u2026` : v;
  }

  return Object.keys(out).length ? out : undefined;
}

function buildRequestHeadersSummary(config) {
  const method = config.method?.toLowerCase();
  const raw = config.headers || {};
  const merged = {};

  for (const source of [raw.common, raw[method], raw]) {
    if (!source || typeof source !== 'object') continue;
    for (const [key, val] of Object.entries(source)) {
      if (typeof val === 'object' && val !== null) continue;
      if (SENSITIVE_HEADERS.has(key.toLowerCase())) continue;
      merged[key] = val;
    }
  }

  return Object.keys(merged).length ? merged : undefined;
}

function buildRequestBodySummary(data) {
  if (data == null) {
    return undefined;
  }
  if (Array.isArray(data)) {
    return { type: 'array', length: data.length };
  }
  if (typeof data !== 'object') {
    return { type: typeof data };
  }

  const summary = {};

  if (data.actionHash) {
    summary.actionHash = data.actionHash;
  }
  if (data.metadata && typeof data.metadata === 'object') {
    if (data.metadata.source) {
      summary.eventSource = data.metadata.source;
    }
    if (data.metadata.type) {
      summary.eventType = data.metadata.type;
    }
  }
  if (data.updatedMap && typeof data.updatedMap === 'object') {
    summary.updatedMapTaskCount = Object.keys(data.updatedMap).length;
  }
  if (data.createdMap && typeof data.createdMap === 'object') {
    summary.createdMapTaskCount = Object.keys(data.createdMap).length;
  }
  if (data.projectIds != null) {
    summary.projectIdsCount = Array.isArray(data.projectIds) ? data.projectIds.length : 1;
  }
  if (data.taskId != null) {
    summary.taskId = data.taskId;
  }
  if (data.ganttId != null) {
    summary.ganttId = data.ganttId;
  }

  if (!Object.keys(summary).length) {
    summary.payloadKeys = Object.keys(data).slice(0, 20);
  }

  return summary;
}

function responseDataForMeta(data) {
  if (data === undefined) {
    return undefined;
  }
  if (Array.isArray(data)) {
    return { items: data };
  }
  if (data !== null && typeof data === 'object') {
    return data;
  }

  return { value: data };
}

function buildNetworkFailureMeta(error) {
  const out = {
    code: error.code,
    message: error.message,
  };

  const req = error.request;

  if (req && typeof req === 'object') {
    if (req.status != null) {
      out.browserRequestStatus = req.status;
    }
    if (req.readyState != null) {
      out.readyState = req.readyState;
    }
    if (req.responseURL) {
      out.responseURL = req.responseURL;
    }
  }

  return out;
}

// Best-effort human-readable "why" for a no-HTTP-response failure, surfaced into Bugsnag
// metadata (APIRESPONSE.networkFailureReason) so the cause is visible without digging.
// Derived from whatever signals we have; `navigatorOnLine` comes from navigator.onLine.
export function describeNetworkFailure(error, navigatorOnLine) {
  if (navigatorOnLine === false) {
    return 'Browser reports no internet connection (navigator.onLine = false)';
  }

  const code = typeof error?.code === 'string' ? error.code : '';

  if (code === 'ECONNABORTED') {
    return 'Request aborted before any response — usually a client-side timeout (ECONNABORTED)';
  }
  if (code === 'ETIMEDOUT') {
    return 'Connection timed out with no response (ETIMEDOUT)';
  }
  if (code === 'ERR_NETWORK') {
    return 'Server unreachable — the request never received a response (ERR_NETWORK)';
  }
  if (code === 'ERR_CANCELED') {
    return 'Request was canceled before completing (ERR_CANCELED)';
  }

  const message = typeof error?.message === 'string' ? error.message : '';

  if (/timeout/i.test(message)) {
    return 'Request timed out before any response';
  }
  if (message === NETWORK_MESSAGE) {
    return 'No response received — connection failed (offline, DNS, CORS, TLS, or server down)';
  }
  if (code) {
    return `No response received (code=${code})`;
  }

  return 'No response received (cause could not be determined)';
}

export function buildAxiosErrorMeta(error, clientKey) {
  const config = error.config;
  const status = error.response?.status;
  const requestPath = resolveRequestPath(config);
  const baseMessage = typeof error?.message === 'string' ? error.message : String(error);

  let errorName;

  if (status != null && isStoreApiGatewayResponse(error.response)) {
    // Infra/edge HTML 5xx — one path-less bucket, never STORE_API_ERROR_502_<route>.
    errorName = STORE_API_ERROR_GATEWAY;
  } else if (status != null) {
    // Real API response → keep status + path (see buildStoreApiServerErrorName).
    errorName = buildStoreApiServerErrorName(status, config, error.response);
  } else if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    // No response, timed out → one path-less bucket for ALL timeouts.
    errorName = buildStoreApiNetworkErrorName('TIMEOUT');
  } else if (error.message === NETWORK_MESSAGE) {
    // No response, connection failed → one path-less bucket for ALL of them.
    errorName = buildStoreApiNetworkErrorName('CONNECTION');
  } else {
    // No response, unclassified → one path-less bucket.
    errorName = buildStoreApiNetworkErrorName('UNKNOWN');
  }

  const capturedMessage = requestPath
    ? `${baseMessage} | ${requestPath}`
    : baseMessage;

  const meta = {};

  if (config) {
    const request = {
      client: clientKey,
      method: config.method,
      url: config.url,
      baseURL: config.baseURL,
    };

    if (requestPath) {
      request.path = requestPath;
    }
    if (config.meta?.requestStartedAt != null) {
      request.requestStartedAt = config.meta.requestStartedAt;
      request.durationMs = new Date().getTime() - config.meta.requestStartedAt;
    }
    if (config.timeout != null) {
      request.timeoutMs = config.timeout;
    }
    if (config.responseType != null) {
      request.responseType = config.responseType;
    }
    if (config.withCredentials === true) {
      request.withCredentials = true;
    }
    if (config.meta?.requestId) {
      request.requestId = config.meta.requestId;
    }
    const headersSummary = buildRequestHeadersSummary(config);

    if (headersSummary) {
      request.headers = headersSummary;
    }
    const bodySummary = buildRequestBodySummary(config.meta?.data);

    if (bodySummary) {
      request.bodySummary = bodySummary;
    }
    meta.APIREQUEST = request;
  }

  if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
    meta.APIRESPONSE = meta.APIRESPONSE || {};
    meta.APIRESPONSE.navigatorOnLine = navigator.onLine;
  }

  if (error.response) {
    const res = error.response;
    const apiResponse = {
      status: res.status,
      statusText: res.statusText,
    };
    const businessCode = extractBusinessCode(res);

    if (businessCode != null) {
      apiResponse.businessCode = businessCode;
    }
    const wrapped = responseDataForMeta(res.data);

    if (wrapped !== undefined) {
      apiResponse.data = wrapped;
    }
    const hdrs = pickResponseHeadersForDebug(res.headers);

    if (hdrs) {
      apiResponse.headers = hdrs;
    }
    if (isStoreApiGatewayResponse(res)) {
      apiResponse.gatewayFailureReason = 'Edge/gateway returned HTML (maintenance or bad gateway), not an API JSON body';
    }
    meta.APIRESPONSE = { ...meta.APIRESPONSE, ...apiResponse };
  } else {
    const navigatorOnLine = meta.APIRESPONSE?.navigatorOnLine;

    meta.APIRESPONSE = {
      ...meta.APIRESPONSE,
      ...buildNetworkFailureMeta(error),
      networkFailureReason: describeNetworkFailure(error, navigatorOnLine),
    };
  }

  if (error.isAxiosError === true) {
    meta.APIRESPONSE = { ...meta.APIRESPONSE, isAxiosError: true };
  }

  return { meta, errorName, capturedMessage };
}
